import { Controller } from "@hotwired/stimulus"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PASSWORD_MIN_LENGTH = 8
const PASSWORD_STRENGTH_REGEX = /^(?=.*[A-Za-z])(?=.*\d).+$/

export default class extends Controller {
  static targets = [
    "emailInput",
    "emailMessage",
    "emailIcon",
    "passwordInput",
    "passwordMessage",
    "passwordIcon",
    "passwordConfirmationInput",
    "passwordConfirmationMessage"
  ]

  static values = {
    emailRequired: String,
    emailInvalid: String,
    passwordTooShort: String,
    passwordWeak: String,
    passwordConfirmationRequired: String,
    passwordConfirmationMismatch: String,
    passwordMinLength: Number
  }

  validateOnSubmit(event) {
    const isEmailValid = this.validateEmail()
    const isPasswordValid = this.validatePassword()
    const isPasswordConfirmationValid = this.validatePasswordConfirmation()

    if (!(isEmailValid && isPasswordValid && isPasswordConfirmationValid)) {
      event.preventDefault()
    }
  }

  validateEmail() {
    if (!this.hasEmailInputTarget) return true

    const email = this.emailInputTarget.value.trim()

    if (email.length === 0) {
      this.setFieldState({
        input: this.emailInputTarget,
        message: this.emailMessageTarget,
        icon: this.hasEmailIconTarget ? this.emailIconTarget : null,
        kind: "error",
        text: this.emailRequiredMessage()
      })
      return false
    }

    if (!EMAIL_REGEX.test(email)) {
      this.setFieldState({
        input: this.emailInputTarget,
        message: this.emailMessageTarget,
        icon: this.hasEmailIconTarget ? this.emailIconTarget : null,
        kind: "error",
        text: this.emailInvalidMessage()
      })
      return false
    }

    this.setFieldState({
      input: this.emailInputTarget,
      message: this.emailMessageTarget,
      icon: this.hasEmailIconTarget ? this.emailIconTarget : null,
      kind: "clear"
    })
    return true
  }

  validatePassword() {
    if (!this.hasPasswordInputTarget) return true

    const password = this.passwordInputTarget.value
    const passwordMinLength = this.passwordMinLength()

    if (password.length === 0) {
      this.setFieldState({
        input: this.passwordInputTarget,
        message: this.passwordMessageTarget,
        icon: this.hasPasswordIconTarget ? this.passwordIconTarget : null,
        kind: "error",
        text: this.passwordTooShortMessage(passwordMinLength)
      })
      return false
    }

    if (password.length < passwordMinLength) {
      this.setFieldState({
        input: this.passwordInputTarget,
        message: this.passwordMessageTarget,
        icon: this.hasPasswordIconTarget ? this.passwordIconTarget : null,
        kind: "error",
        text: this.passwordTooShortMessage(passwordMinLength)
      })
      return false
    }

    if (!PASSWORD_STRENGTH_REGEX.test(password)) {
      this.setFieldState({
        input: this.passwordInputTarget,
        message: this.passwordMessageTarget,
        icon: this.hasPasswordIconTarget ? this.passwordIconTarget : null,
        kind: "warning",
        text: this.passwordWeakMessage()
      })
      return true
    }

    this.setFieldState({
      input: this.passwordInputTarget,
      message: this.passwordMessageTarget,
      icon: this.hasPasswordIconTarget ? this.passwordIconTarget : null,
      kind: "clear"
    })
    return true
  }

  validatePasswordConfirmation() {
    if (!this.hasPasswordConfirmationInputTarget) return true

    const passwordConfirmation = this.passwordConfirmationInputTarget.value
    const password = this.hasPasswordInputTarget ? this.passwordInputTarget.value : ""

    if (passwordConfirmation.length === 0) {
      this.setFieldState({
        input: this.passwordConfirmationInputTarget,
        message: this.passwordConfirmationMessageTarget,
        icon: null,
        kind: "error",
        text: this.passwordConfirmationRequiredMessage()
      })
      return false
    }

    if (passwordConfirmation !== password) {
      this.setFieldState({
        input: this.passwordConfirmationInputTarget,
        message: this.passwordConfirmationMessageTarget,
        icon: null,
        kind: "error",
        text: this.passwordConfirmationMismatchMessage()
      })
      return false
    }

    this.setFieldState({
      input: this.passwordConfirmationInputTarget,
      message: this.passwordConfirmationMessageTarget,
      icon: null,
      kind: "clear"
    })
    return true
  }

  setFieldState({ input, message, icon, kind, text = "" }) {
    this.setInputClasses(input, kind)
    this.setMessage(message, kind, text)
    this.setIcon(icon, kind)
  }

  setInputClasses(input, kind) {
    input.classList.remove("tc-auth-input-error", "tc-auth-input-warning")

    if (kind === "error") input.classList.add("tc-auth-input-error")
    if (kind === "warning") input.classList.add("tc-auth-input-warning")
  }

  setMessage(message, kind, text) {
    if (!message) return

    message.classList.remove("hidden", "tc-auth-error-message", "tc-auth-warning-message")

    if (kind === "clear") {
      message.textContent = ""
      message.classList.add("hidden", "tc-auth-error-message")
      return
    }

    message.textContent = text
    message.classList.add(kind === "warning" ? "tc-auth-warning-message" : "tc-auth-error-message")
  }

  setIcon(icon, kind) {
    if (!icon) return

    icon.classList.remove("hidden", "tc-auth-icon-error", "tc-auth-icon-warning")

    if (kind === "clear") {
      icon.classList.add("hidden")
      return
    }

    icon.classList.add(kind === "warning" ? "tc-auth-icon-warning" : "tc-auth-icon-error")
  }

  emailRequiredMessage() {
    return this.hasEmailRequiredValue ? this.emailRequiredValue : "Email is required."
  }

  emailInvalidMessage() {
    return this.hasEmailInvalidValue ? this.emailInvalidValue : "Please enter a valid email address."
  }

  passwordTooShortMessage(minLength) {
    if (this.hasPasswordTooShortValue) return this.passwordTooShortValue

    return `Password must contain at least ${minLength} characters.`
  }

  passwordWeakMessage() {
    return this.hasPasswordWeakValue ? this.passwordWeakValue : "Password is weak. Try adding letters and numbers."
  }

  passwordConfirmationRequiredMessage() {
    return this.hasPasswordConfirmationRequiredValue ? this.passwordConfirmationRequiredValue : "Please confirm your password."
  }

  passwordConfirmationMismatchMessage() {
    return this.hasPasswordConfirmationMismatchValue ? this.passwordConfirmationMismatchValue : "Password confirmation does not match."
  }

  passwordMinLength() {
    return this.hasPasswordMinLengthValue ? this.passwordMinLengthValue : PASSWORD_MIN_LENGTH
  }
}
